import { useEffect, useCallback, useState } from 'react';
import React from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Button, Drawer, Modal, Popconfirm, Spin, Table, message } from 'antd';
import { Dayjs } from 'dayjs';

import '../../CRM/Antd.css';
import sessionManager, { eVariableType } from '../../SessionManager';
import { GetErrorMessage } from '../../Utility';
import api from '../../api';
import stringres from '../../translations/stringres';
import ViaggioAddEdit from './ViaggioAddEdit';
import ViaggioDettagli from './ViaggioDettagli';

interface viaggiProps {
    descrizione: string;
    dataInizio: Dayjs;
    dataFine: Dayjs;
    refresh: boolean;
}

type viaggioModel = {
    idViaggio: number | null;
    descrizioneViaggio: string;
    dataInizio: Dayjs;
    dataFine: Dayjs | null;
    codNazione: string;
};

export default function ViaggiGrid(props: viaggiProps) {
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<viaggioModel[]>([]);
    const [idViaggio, setIDViaggio] = useState(null);
    const [currentrow, setCurrentrow] = useState<viaggioModel>();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openDettaglioViaggio, setOpenDettaglioViaggio] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        try {
            let query = '';

            if (props.descrizione !== null && props.descrizione !== '')
                query = query + '&descrizione=' + encodeURIComponent(props.descrizione);
            if (props.dataInizio.isValid()) query = query + '&DataDa=' + props.dataInizio.toString();
            if (props.dataFine.isValid()) query = query + '&DataA=' + props.dataFine.toString();

            const result: any = await api.get(
                'CRM/viaggi?idSocieta=' + sessionManager.get(eVariableType.IDSocieta) + query
            );

            if (result != null) {
                if (Array.isArray(result.data) && result.data.length > 0) {
                    setData(result.data);
                } else {
                    setData([]);
                }
            }
        } catch (errorx) {
            message.error(GetErrorMessage(errorx));
        } finally {
            setIsLoading(false);
        }
    }, [props]);

    useEffect(() => {
        fetchData();
    }, [fetchData, props]);

    const columns = [
        {
            title: stringres.t('datainizio'),
            dataIndex: 'dataPartenza',
            width: 125,
            render: (text) => (
                <div>
                    {new Date(text).toLocaleString(localStorage.getItem('codLingua')?.toString(), {
                        dateStyle: 'short',
                    })}
                </div>
            ),
        },
        {
            title: stringres.t('datafine'),
            dataIndex: 'dataRientro',
            width: 125,
            render: (text) =>
                text !== null ? (
                    <div>
                        {new Date(text).toLocaleString(localStorage.getItem('codLingua')?.toString(), {
                            dateStyle: 'short',
                        })}
                    </div>
                ) : (
                    <div></div>
                ),
        },
        {
            title: stringres.t('descrizione'),
            dataIndex: 'descrizioneViaggio',
            render: (text, record) => (
                <button className="link-button" onClick={() => onclick(record)}>
                    {text}
                </button>
            ),
        },
        { title: stringres.t('nazione'), dataIndex: 'codNazione', width: 50 },
        {
            title: '',
            width: 50,
            render: (record) => (
                <Popconfirm
                    title={stringres.t('elimina').toString()}
                    description={stringres.t('eliminare?').toString()}
                    onConfirm={() => onDelete(record)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button icon={<DeleteOutlined />}></Button>
                </Popconfirm>
            ),
        },
    ];

    async function onDelete(rec: any) {
        try {
            setIsLoadingDelete(true);
            if (rec != null) {
                const result = await api.delete(
                    'CRM/Viaggi/Delete?idSocieta=' +
                        sessionManager.get(eVariableType.IDSocieta) +
                        '&idViaggio=' +
                        rec.idViaggio
                );
                if (result.status === 200) {
                    message.success(stringres.t('successo').toString());

                    fetchData();
                } else message.error(result.statusText);
            }
        } catch (error) {
            message.error(error.response.data.message, 5);
        } finally {
            setIsLoadingDelete(false);
        }
    }

    function onclick(record: any) {
        setCurrentrow(record);
        setOpenDettaglioViaggio(true);
        setIDViaggio(record.idViaggio);
    }

    function onCancelEdit() {
        setIsModalOpen(false);
    }

    const rowKeys = ['descrizioneViaggio', 'dataInizio'];

    return (
        <div>
            <Spin spinning={isLoading || isLoadingDelete} size="large">
                <Table<viaggioModel>
                    scroll={{ x: 'max-content' }}
                    rowKey={(row) => rowKeys.map((key) => row[key]).join('-')}
                    columns={columns}
                    dataSource={data}
                ></Table>
            </Spin>
            <Modal width={400} open={isModalOpen} onCancel={onCancelEdit} destroyOnClose={true}>
                <ViaggioAddEdit width="400" idViaggio={idViaggio} onClose={onCancelEdit}></ViaggioAddEdit>
            </Modal>
            <Drawer
                style={{ backgroundColor: '#eef2f4' }}
                open={openDettaglioViaggio}
                title={
                    <span className="App-title-dark">
                        {stringres.t('viaggio').toString() + ': ' + currentrow?.descrizioneViaggio}
                    </span>
                }
                width="90%"
                onClose={() => setOpenDettaglioViaggio(false)}
                footer={null}
                destroyOnClose={true}
            >
                <ViaggioDettagli idViaggio={idViaggio}></ViaggioDettagli>
            </Drawer>
        </div>
    );
}
